import { useEffect, useState } from 'react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { getEligible } from '@/lib/pickups-api';
import type { EligibleRow } from '@/lib/pickups-types';

/**
 * Eligible Members - a browsable, paginated list of the eligible-bucket roster from the cached
 * eligibility CSV. Search (q -> memberId) + kit-stage filter + "Load more" cursor pagination.
 * Clicking a row deep-links into Member Lookup for that member.
 */

interface EligibleMembersProps {
  /** Deep-link a member id into Member Lookup and switch the active page there. */
  onOpenMember: (memberId: string) => void;
}

const PAGE_LIMIT = 50;

function stdAddressOf(row: EligibleRow): string {
  const street = row.stdAddress || row.address;
  const zip = row.stdZip4 || row.zip;
  return [street, zip].filter(Boolean).join(' · ');
}

export function EligibleMembers({ onOpenMember }: EligibleMembersProps) {
  const [rows, setRows] = useState<EligibleRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter state (committed on submit, not per-keystroke).
  const [q, setQ] = useState('');
  const [kitStage, setKitStage] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [appliedKitStage, setAppliedKitStage] = useState('');

  // Distinct kit stages observed in the loaded rows, for the filter select.
  const [kitStageOptions, setKitStageOptions] = useState<string[]>([]);

  async function load(reset: boolean, cursor?: string | undefined) {
    if (reset) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }
    try {
      const res = await getEligible({
        limit: PAGE_LIMIT,
        cursor,
        q: appliedQ || undefined,
        kitStage: appliedKitStage || undefined,
      });
      setTotal(res.total);
      setNextCursor(res.nextCursor);
      setRows((prev) => {
        const merged = reset ? res.items : [...prev, ...res.items];
        // Refresh the kit-stage filter options from whatever we have loaded.
        const stages = Array.from(new Set(merged.map((r) => r.kitStage).filter(Boolean))).sort();
        setKitStageOptions(stages);
        return merged;
      });
    } catch (e) {
      const status = (e as { status?: number }).status;
      setError(`Could not load eligible members${status ? ` (HTTP ${status})` : ''}.`);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  // Reload when the applied filters change (and on first mount).
  useEffect(() => {
    void load(true, undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedQ, appliedKitStage]);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setAppliedQ(q.trim());
    setAppliedKitStage(kitStage);
  }

  return (
    <div className="page-pad">
      <header>
        <div className="section-eyebrow">CDX Pickups · Roster</div>
        <h1 className="section-title">
          Eligible <em>Members</em>
        </h1>
        <div className="section-rule" />
        <p className="section-lede">
          Members whose address USPS confirms is serviceable for a carrier pickup. Search by id,
          filter by kit stage, and open a member to schedule.
        </p>
      </header>

      {/* ── Stat tiles ────────────────────────────────────────────── */}
      <section>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 'var(--gap-card)',
          }}
        >
          <div className="stat accent">
            <div className="label">Total Eligible</div>
            <div className="value tab-num">{total == null ? '--' : total.toLocaleString()}</div>
            <div className="sub">eligibility cache</div>
          </div>
          <div className="stat">
            <div className="label">Loaded</div>
            <div className="value tab-num">{rows.length.toLocaleString()}</div>
            <div className="sub">{nextCursor ? 'more available' : 'all loaded'}</div>
          </div>
          <div className="stat">
            <div className="label">Kit Stages</div>
            <div className="value tab-num">{kitStageOptions.length || '--'}</div>
            <div className="sub">in loaded rows</div>
          </div>
        </div>
      </section>

      {/* ── Filters ───────────────────────────────────────────────── */}
      <section>
        <form className="card" onSubmit={onSubmit}>
          <div className="field-row" style={{ marginBottom: 0, gridTemplateColumns: '1fr 200px auto' }}>
            <div>
              <label className="field-label" htmlFor="em-q">Search (member id)</label>
              <input
                id="em-q"
                className="text-input"
                placeholder="e.g. 100245"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div>
              <label className="field-label" htmlFor="em-stage">Kit stage</label>
              <select
                id="em-stage"
                className="select-input"
                value={kitStage}
                onChange={(e) => setKitStage(e.target.value)}
              >
                <option value="">All stages</option>
                {kitStageOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                Apply
              </button>
            </div>
          </div>
        </form>
      </section>

      {/* ── Table ─────────────────────────────────────────────────── */}
      <section>
        <div className="block-head">
          Eligible Roster
          <span className="aux">{rows.length} loaded{total != null ? ` of ${total.toLocaleString()}` : ''}</span>
        </div>

        {loading ? (
          <div className="card">
            <LoadingSpinner label="Loading eligible members…" />
          </div>
        ) : error ? (
          <div className="note danger">
            <b>Error.</b> {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="note">No eligible members match the current filters.</div>
        ) : (
          <>
            <div className="card card-flush">
              <table className="dt">
                <thead>
                  <tr>
                    <th>Member ID</th>
                    <th>Language</th>
                    <th>Kit Stage</th>
                    <th>City / State</th>
                    <th>Standardized Address</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.memberId}
                      onClick={() => onOpenMember(r.memberId)}
                      style={{ cursor: 'pointer' }}
                      title="Open in Member Lookup"
                    >
                      <td className="mono">{r.memberId}</td>
                      <td>{r.language || '--'}</td>
                      <td className="mono">{r.kitStage || '--'}</td>
                      <td>{[r.city, r.state].filter(Boolean).join(', ') || '--'}</td>
                      <td>{stdAddressOf(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {nextCursor && (
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center' }}>
                <button
                  type="button"
                  className="btn"
                  onClick={() => void load(false, nextCursor)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
